import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { Lock, Mail, AlertCircle, Shield, Key } from 'lucide-react';
import { useAuth } from '../../contexts/SupabaseAuthContext';
import { is2FAVerified, clear2FAVerification, verifyTOTP, set2FAVerified } from '../../utils/2fa';
import AdminSecurityWrapper from '../../components/AdminSecurityWrapper';

type LoginStep = '2fa-verify' | 'credentials';

const AdminLogin: React.FC = () => {
  const { user, signIn } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState<LoginStep>('2fa-verify');

  // Clear 2FA verification when component mounts
  useEffect(() => {
    clear2FAVerification();
    // Luôn bắt đầu với bước xác thực 2FA
    setCurrentStep('2fa-verify');
  }, []);

  // Redirect if user is authenticated and 2FA is verified
  if (user && is2FAVerified()) {
    return <Navigate to="/vmon-admin" replace />;
  }

  const handle2FAVerification = async () => {
    if (!verificationCode.trim()) {
      setError('Vui lòng nhập mã xác thực');
      return;
    }

    if (verificationCode.length !== 6) {
      setError('Mã xác thực phải có 6 chữ số');
      return;
    }

    setIsLoading(true);
    setError('');

    // Simulate verification delay
    await new Promise(resolve => setTimeout(resolve, 500));

    const isValid = verifyTOTP(verificationCode.trim());

    if (isValid) {
      set2FAVerified(true);
      setCurrentStep('credentials');
      setVerificationCode('');
    } else {
      setError('Mã xác thực không đúng. Vui lòng kiểm tra lại mã từ Google Authenticator.');
    }

    setIsLoading(false);
  };

  const handleCredentialsSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    const { error: signInError } = await signIn(email, password);
    if (signInError) {
      console.error('Login error:', signInError);
      
      // Provide more specific error messages
      if (signInError.message?.includes('Invalid login credentials')) {
        setError('Email hoặc mật khẩu không đúng. Vui lòng kiểm tra lại thông tin đăng nhập.');
      } else if (signInError.message?.includes('Email not confirmed')) {
        setError('Email chưa được xác nhận. Vui lòng kiểm tra email và xác nhận tài khoản.');
      } else {
        setError(`Lỗi đăng nhập: ${signInError.message || 'Có lỗi xảy ra, vui lòng thử lại.'}`);
      }
      setIsLoading(false);
      return;
    }

    // Login successful, redirect to admin
    window.location.href = '/vmon-admin';
    setIsLoading(false);
  };

  const handleBackTo2FA = () => {
    setCurrentStep('2fa-verify');
    setEmail('');
    setPassword('');
    setError('');
    clear2FAVerification();
  };

  return (
    <AdminSecurityWrapper>
      <div className="min-h-screen bg-gray-50">
        {/* Show 2FA verification screen (BẮT BUỘC - không có nút bỏ qua) */}
        {currentStep === '2fa-verify' && (
          <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
            <div className="sm:mx-auto sm:w-full sm:max-w-md">
              <div className="text-center">
                <Shield className="mx-auto h-12 w-12 text-blue-600" />
                <h2 className="mt-6 text-3xl font-bold text-gray-900">
                  Xác thực bảo mật
                </h2>
                <p className="mt-2 text-sm text-gray-600">
                  Nhập mã 6 chữ số từ Google Authenticator để tiếp tục
                </p>
              </div>
            </div>

            <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
              <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
                <div className="space-y-6">
                  {error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded flex items-start space-x-2">
                      <AlertCircle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                      <div className="text-sm">{error}</div>
                    </div>
                  )}

                  <div>
                    <label htmlFor="verification-code" className="block text-sm font-medium text-gray-700 mb-2">
                      Mã xác thực từ Google Authenticator
                    </label>
                    <div className="mt-1 relative">
                      <Key className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                      <input
                        id="verification-code"
                        type="text"
                        maxLength={6}
                        placeholder="000000"
                        className="pl-10 appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-center text-lg font-mono"
                        value={verificationCode}
                        onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            handle2FAVerification();
                          }
                        }}
                        autoFocus
                      />
                    </div>
                    <p className="mt-2 text-xs text-gray-500">
                      Mã sẽ thay đổi mỗi 30 giây
                    </p>
                  </div>

                  <div>
                    <button
                      onClick={handle2FAVerification}
                      disabled={isLoading || verificationCode.length !== 6}
                      className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-semibold"
                    >
                      {isLoading ? 'Đang xác thực...' : 'Xác thực và tiếp tục'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Show credentials login screen (after 2FA verification) */}
        {currentStep === 'credentials' && (
          <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
            <div className="sm:mx-auto sm:w-full sm:max-w-md">
              <div className="text-center">
                <Lock className="mx-auto h-12 w-12 text-green-600" />
                <h2 className="mt-6 text-3xl font-bold text-gray-900">
                  Đăng nhập Admin
                </h2>
                <p className="mt-2 text-sm text-gray-600">
                  Nhập thông tin đăng nhập để truy cập admin
                </p>
                <div className="mt-2 flex items-center justify-center text-sm text-green-600">
                  <Shield className="h-4 w-4 mr-1" />
                  <span>✅ Đã xác thực 2FA thành công</span>
                </div>
              </div>
            </div>

            <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
              <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
                <form className="space-y-6" onSubmit={handleCredentialsSubmit}>
                  {error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded flex items-start space-x-2">
                      <AlertCircle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                      <div className="text-sm">{error}</div>
                    </div>
                  )}

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                      Email
                    </label>
                    <div className="mt-1 relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                      <input
                        id="email"
                        name="email"
                        type="email"
                        required
                        className="pl-10 appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Nhập email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        autoFocus
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                      Mật khẩu
                    </label>
                    <div className="mt-1 relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                      <input
                        id="password"
                        name="password"
                        type="password"
                        required
                        className="pl-10 appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Nhập mật khẩu"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="flex space-x-4">
                    <button
                      type="submit"
                      disabled={isLoading}
                      className="flex-1 bg-green-600 text-white py-3 px-4 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-semibold"
                    >
                      {isLoading ? 'Đang đăng nhập...' : 'Đăng nhập'}
                    </button>
                    <button
                      type="button"
                      onClick={handleBackTo2FA}
                      disabled={isLoading}
                      className="flex-1 bg-gray-300 text-gray-700 py-3 px-4 rounded-lg hover:bg-gray-400 disabled:opacity-50 transition-colors font-semibold"
                    >
                      Quay lại
                    </button>
                  </div>
                </form>

                <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-md">
                  <div className="text-center text-sm text-green-800">
                    <p className="font-semibold mb-2">✅ Bảo mật 2 lớp đã kích hoạt</p>
                    <p className="text-xs">
                      Thông tin đăng nhập được bảo vệ bởi xác thực 2FA bắt buộc
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminSecurityWrapper>
  );
};

export default AdminLogin;